import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import logo from 'src/images/logo-sm.png';
import {
  Button,
  Collapse,
  Container,
  Icon,
  InputSelect,
  Link,
  Nav,
  Navbar,
  NavbarToggler,
  NavItem,
  useApp
} from 'straightline-ui';
import { ENTITY_TYPE, STATUS, statusType } from 'straightline-utils/constants';

// Note that there is a NavLink reactstrap item. We do not use it,
// but create our own that wraps a react-router Link
const NavLink = ({ to, className, location, ...rest }) => (
  <Link
    {...rest}
    to={to}
    className={`${className} nav-link ${location.pathname === to && 'active'}`}
  />
);

export default function Sidebar() {
  const [open, setOpen] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const app = useApp();

  const toggle = () => {
    setOpen(!open);
  };

  const close = () => {
    if (open) {
      setOpen(false);
    }
  };

  const back = () => {
    history.goBack();
  };

  return (
    <Navbar light expand="lg" className="navbar-vertical fixed-left">
      <Container fluid>
        <Button
          color="link"
          className="py-1 px-2 text-dark d-block d-md-none"
          onClick={back}
        >
          <Icon type="chevron-left" />
        </Button>
        <Link className="navbar-brand" to="/">
          <img src={logo} className="navbar-brand-img mx-auto" alt="..." />
        </Link>
        <NavbarToggler onClick={toggle} />
        <Collapse navbar isOpen={open}>
          <AccountSwitcher />
          <hr className="my-3 d-none d-md-block" />
          <Nav navbar>
            <NavItem onClick={close}>
              <NavLink to="/dashboard" location={location}>
                <Icon type="home" /> Dashboard
              </NavLink>
            </NavItem>
            <NavItem onClick={close}>
              <NavLink to="/shipment-matches" location={location}>
                <Icon type="clipboard" /> Load Board
              </NavLink>
            </NavItem>
            <NavItem onClick={close}>
              <NavLink to="/shipment-offers" location={location}>
                <Icon type="dollar-sign" /> Load Offers
              </NavLink>
            </NavItem>
            <NavItem onClick={close}>
              <NavLink to="/shipments" location={location}>
                <Icon type="package" /> Booked Loads
              </NavLink>
            </NavItem>
            <NavItem onClick={close}>
              <NavLink to="/shipment-requests" location={location}>
                <Icon type="truck" /> Trucks
              </NavLink>
            </NavItem>
            <NavItem onClick={close}>
              <NavLink to="/shipment-transfers" location={location}>
                <Icon type="file-text" /> Payments
              </NavLink>
            </NavItem>
            <NavItem onClick={close}>
              <NavLink to="/reports" location={location}>
                <Icon type="bar-chart" /> Reports
              </NavLink>
            </NavItem>
            <div className="px-4 my-4">
              <Button color="primary" block to="/shipment-matches">
                <Icon type="clipboard" className="mr-2" />
                Find Loads
              </Button>
            </div>
          </Nav>
          <Alerts close={close} location={location} />
          <div className="mt-auto">
            <hr className="my-3" />
            <Nav navbar className="mb-md-3">
              <NavItem onClick={close}>
                <NavLink to="/settings" location={location}>
                  <Icon type="settings" /> Settings
                </NavLink>
              </NavItem>
              <NavItem onClick={close}>
                <NavLink to="/support" location={location}>
                  <Icon type="send" /> Support
                </NavLink>
              </NavItem>
              <NavItem onClick={close}>
                <NavLink to="/guide" location={location}>
                  <Icon type="compass" /> Guide
                </NavLink>
              </NavItem>
              <NavItem className="nav-item">
                <div className="nav-link pointer" onClick={app.logout}>
                  <Icon type="log-out" /> Log Out
                </div>
              </NavItem>
            </Nav>
          </div>
        </Collapse>
      </Container>
    </Navbar>
  );
}

function Alerts({ close, location }) {
  const app = useApp();

  const { result: etaResults } = app.service('api/shipments').useFind(
    {
      query: {
        $limit: 25,
        $sort: { pickup_date: -1 },
        status_type: { $nin: [STATUS.DELIVERED, STATUS.CANCELLED] }
      }
    },
    { keepPreviousData: true, refetchInterval: 60000 }
  );

  const { result: podResults } = app.service('api/shipments').useFind(
    {
      $include: ['pod'],
      query: {
        $limit: 25,
        $sort: { pickup_date: -1 },
        status_type: { $in: [STATUS.DELIVERY, STATUS.DELIVERED] }
      }
    },
    { keepPreviousData: true, refetchInterval: 60000 }
  );

  const { result: dispatchShipment } = app.service('api/shipments').useFindOne(
    {
      query: {
        $select: ['id', 'pickup_address', 'delivery_address'],
        $sort: { created_at: -1 },
        shipment_dispatch_id: null,
        status_type: { $nin: [STATUS.DELIVERED, STATUS.CANCELLED] }
      }
    },
    { keepPreviousData: true, refetchInterval: 60000 }
  );

  const etaShipments = etaResults?.data || [];
  const etaShipment = etaShipments.find((shipment) => {
    if (!shipment.eta) {
      return true;
    }
    const type =
      statusType(shipment.status_type).order <= 3 ? 'pickup' : 'delivery';
    return shipment.eta.type !== type;
  });

  const podShipments = podResults?.data || [];
  const podShipment = podShipments.find((shipment) => {
    return !shipment.pod;
  });

  return (
    <React.Fragment>
      <hr className="my-3" />
      <Nav navbar>
        {dispatchShipment && (
          <NavItem onClick={close}>
            <NavLink
              to={`/shipments/${dispatchShipment.id}?modal=driver`}
              location={location}
              className="align-items-start animated fadeIn"
            >
              <Icon type="alert-circle" className="text-danger" />
              <div>
                <div className="text-dar font-weight-bold">
                  Load #{dispatchShipment.id}
                </div>
                <div className="small mt-n1">
                  {dispatchShipment.pickup_address.city} ⇢{' '}
                  {dispatchShipment.delivery_address.city}
                </div>
                <div className="text-danger">Dispatch Driver</div>
              </div>
            </NavLink>
          </NavItem>
        )}
        {etaShipment && (
          <NavItem onClick={close}>
            <NavLink
              to={`/shipments/${etaShipment.id}?modal=eta`}
              location={location}
              className="align-items-start animated fadeIn"
            >
              <Icon type="alert-circle" className="text-danger" />
              <div>
                <div className="text-dar font-weight-bold">
                  Load #{etaShipment.id}
                </div>
                <div className="small mt-n1">
                  {etaShipment.pickup_address.city} ⇢{' '}
                  {etaShipment.delivery_address.city}
                </div>
                <div className="text-danger">
                  Set Estimated{' '}
                  {statusType(etaShipment.status_type).order <= 3
                    ? 'Pickup'
                    : 'Delivery'}{' '}
                </div>
              </div>
            </NavLink>
          </NavItem>
        )}
        {podShipment && (
          <NavItem onClick={close}>
            <NavLink
              to={`/shipments/${podShipment.id}?modal=pod`}
              location={location}
              className="align-items-start animated fadeIn"
            >
              <Icon type="alert-circle" className="text-danger" />
              <div>
                <div className="text-dar font-weight-bold">
                  Load #{podShipment.id}
                </div>
                <div className="small mt-n1">
                  {podShipment.pickup_address.city} ⇢{' '}
                  {podShipment.delivery_address.city}
                </div>
                <div className="text-danger">Upload POD</div>
              </div>
            </NavLink>
          </NavItem>
        )}
      </Nav>
    </React.Fragment>
  );
}

function AccountSwitcher() {
  const app = useApp();
  const tokens = app.auth.tokens.filter((token) => {
    return token.entity_type === ENTITY_TYPE.CARRIER;
  });

  if (tokens.length === 1) {
    return null;
  }

  const token = app.auth.token;

  const options = tokens.map((token) => {
    return {
      label: token.entity_name,
      value: token.token
    };
  });

  const handleChange = async ({ target: { value } }) => {
    if (app.auth.token !== value) {
      window.sessionStorage.setItem('straightline-token', value);
      window.location.reload();
    }
  };

  return (
    <InputSelect value={token} options={options} onChange={handleChange} />
  );
}
